// src/components/Greetings/Greetings.tsx
import React, { useEffect, useMemo } from 'react';
import { Link, useOutletContext } from 'react-router-dom';
import type { LayoutContext } from '@/layout/Layout';

// Swiper
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, Pagination, Navigation } from 'swiper/modules';
import 'swiper/scss';
import 'swiper/scss/pagination';
import 'swiper/scss/navigation';

// UI
import {
    Anchor,
    Box,
    Button,
    Container,
    Loader, rem,
    SimpleGrid,
    Stack,
    Text,
    Title,
} from '@mantine/core';
import { TitleDecor } from '@/components/TitleDecor/TitleDecor';
import { VideoItem } from '@/components/Video/VideoItem';

// Redux
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import {
    loadVideos,
    selectVideosStatus,
    selectVideosError,
    selectUpcomingVideos,
    selectReleasedVideos,
} from '@/features/videos/videosSlice';

import classes from './Greetings.module.scss';

export const Greetings: React.FC = () => {
    const { headerH } = useOutletContext<LayoutContext>();
    const dispatch = useAppDispatch();

    const videosStatus = useAppSelector(selectVideosStatus);
    const videosError  = useAppSelector(selectVideosError);

    const premieres = useAppSelector(selectUpcomingVideos);
    const released  = useAppSelector(selectReleasedVideos);

    useEffect(() => {
        if (videosStatus === 'idle') {
            dispatch(loadVideos());
        }
    }, [videosStatus, dispatch]);

    const latestVideo = useMemo(() => released.slice(0, 12), [released]);

    const isLoading =
        videosStatus === 'loading' && latestVideo.length === 0 && premieres.length === 0;

    if (videosStatus === 'failed') {
        return <div>Error: {String(videosError ?? 'Failed to load videos')}</div>;
    }
    if (isLoading) {
        return <Loader color="gray" />;
    }

    return (
        <Box
            component="section"
            style={{ paddingTop: `calc(20px + ${headerH}px)`}}
            className={classes.sectionHome}
            mx={''}
        >
            {/*<Container className={classes.sectionHomeContainer}>*/}
                <Box className={classes.greetings}>
                    <Stack gap={16} mb={16} className={classes.greetingsText}>
                        <TitleDecor m="0" p="0" c="white">
                            Ask an IFBB Pro Anything!
                        </TitleDecor>

                        <Anchor
                            className={classes.btnQaVideo}
                            title="NapsGear.org Project"
                            href="https://www.napsgear.org/ama.php"
                            target="_blank"
                            rel="noopener noreferrer"
                            fw={600}
                        >
                            See the Q&amp;A videos here!
                            <span className="ml-2 right-icon">&#8594;</span>
                        </Anchor>

                        <Text component="p" c="dimmed">
                            Who better to get your bodybuilding{' '}
                            <Text component="span" style={{ whiteSpace: 'nowrap' }}>
                                advice from than an IFBB Pro?!
                            </Text>
                        </Text>

                        <Anchor
                            // component="a"
                            href="https://www.napsgear.org/ama.php"
                            target="_blank"
                            rel="noopener noreferrer"

                            styles={{
                                root: {
                                    display: 'inline-block',
                                    textDecoration: 'none',
                                    padding: 'var(--mantine-spacing-xs) var(--mantine-spacing-sm)',

                                    background: 'var(--mantine-primary-color-6)',
                                    color: 'var(--mantine-primary-color-light-color)',
                                    transition: 'background-color .15s ease,color .15s ease',
                                    '&:hover': {
                                        background: 'var(--mantine-primary-color-light-hover)',
                                        color: 'var(--mantine-primary-color-light-color)',
                                        textDecoration: 'none',
                                    },
                                    '&:visited': { color: 'var(--mantine-primary-color-light-color)' },
                                },
                            }}
                        >
                            Submit your questions now
                            <Text component="span" style={{ whiteSpace: 'nowrap' }}>and get an answer from an IFBB Pro!</Text>
                        </Anchor>
                    </Stack>


                    {latestVideo.length > 0 && (
                        <Swiper
                            slidesPerView={1}
                            spaceBetween={12}
                            loop
                            autoplay={{ delay: 3000, disableOnInteraction: false }}
                            breakpoints={{
                                768: { slidesPerView: 2, spaceBetween: 12 },
                                992: { slidesPerView: 3, spaceBetween: 12 },


                        }}
                            navigation
                            pagination={{ clickable: true }}
                            modules={[Autoplay, Pagination, Navigation]}
                            id="fpVideo"
                            className={classes.videoSwiper}
                        >
                            {latestVideo.map((item) => (
                                <SwiperSlide key={item.id}>
                                    <VideoItem key={`prem-${item.id}`} item={item} />
                                </SwiperSlide>
                            ))}
                        </Swiper>
                    )}

                    <Box ta="center">
                        <Anchor component={Link} to="/videos" className={classes.moreVideos}>
                            For additional videos, please click here.
                        </Anchor>
                    </Box>


                    {premieres.length > 0 && (
                        <Box className={classes.upcommingList} mt={'lg'}>
                            <Title order={4} mb={'md'}>Upcoming Episodes</Title>

                            <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="md">
                                {premieres.map((item) => (
                                    <VideoItem key={item.id} item={item} />
                                ))}
                            </SimpleGrid>
                        </Box>
                    )}
                </Box>
            {/*</Container>*/}
        </Box>
    );
};
